import React, { useReducer, createContext, useContext, useEffect } from 'react';
import styled, { StyledComponent } from '@emotion/styled';
import type { SerializedStyles } from '@emotion/react';
import type { WPublicWeddingView, WThemeComponentFontView } from '@zola/svc-web-api-ts-client';
import COLORS from '@zola/zola-ui/src/styles/emotion/colors';
import type { FullComponentFontView } from './util/mappers';

export type WebsiteThemeContextStateType = {
  components: {
    Button: StyledComponent<React.ButtonHTMLAttributes<HTMLButtonElement>>;
    CmsEntityBodyLink: StyledComponent<React.AnchorHTMLAttributes<HTMLAnchorElement>>;
    globalBodyFontStyle: SerializedStyles;
    globalHeaderFontStyle: SerializedStyles;
    globalHeaderFontValues: FullComponentFontView;
    globalBodyFontValues: FullComponentFontView;
    globalAccentColor: string;
    globalInputFieldStyles: SerializedStyles;
    cmsEntityComponentHeaderFontValues: FullComponentFontView;
    cmsEntityComponentBodyFontValues: FullComponentFontView;
    cmsEntityComponentBodyFont2Values: FullComponentFontView;
    cmsEntityComponentHeaderFontStyles: SerializedStyles;
    cmsEntityComponentBodyFontStyles: SerializedStyles;
    cmsEntityComponentBodyFont2Styles: SerializedStyles;
    backgroundColors: {
      nav: string;
      homepageContent: string;
      content: string;
      global: string;
    };
  };
  highlightedComponents: string[];
  wedding?: WPublicWeddingView;
  inPreview?: boolean;
  pageDataContext?: {
    isHidden?: boolean;
    homepageHeaderImage?: string;
  };
};

export const DEFAULT_HEADER_FONT_VALUES: FullComponentFontView = {
  fontFamily: 'Proxima Nova',
  isCursive: false,
  fontSize: 40,
  letterSpacing: 0,
  lineHeight: 1.6,
  textTransform: ('NONE' as unknown) as WThemeComponentFontView.TextTransformEnum,
  color: '000000',
  fontWeight: ('NORMAL' as unknown) as WThemeComponentFontView.FontWeightEnum,
};
export const DEFAULT_BODY_FONT_VALUES = {
  fontFamily: 'Proxima Nova',
  isCursive: false,
  fontSize: 20,
  letterSpacing: 0,
  lineHeight: 1.6,
  textTransform: ('NONE' as unknown) as WThemeComponentFontView.TextTransformEnum,
  color: '000000',
  fontWeight: ('NORMAL' as unknown) as WThemeComponentFontView.FontWeightEnum,
};
export const DEFAULT_ACCENT_COLOR = COLORS.NAVY.substring(1);

export const DEFAULT_BACKGROUND_COLOR = 'FFFFFF';

const initialState: WebsiteThemeContextStateType = {
  components: {
    Button: styled.button``,
    CmsEntityBodyLink: styled.a``,
    globalBodyFontStyle: { name: '', styles: '' },
    globalHeaderFontStyle: { name: '', styles: '' },
    globalBodyFontValues: DEFAULT_BODY_FONT_VALUES,
    globalHeaderFontValues: DEFAULT_HEADER_FONT_VALUES,
    globalAccentColor: DEFAULT_ACCENT_COLOR,
    globalInputFieldStyles: { name: '', styles: '' },
    cmsEntityComponentHeaderFontValues: DEFAULT_HEADER_FONT_VALUES,
    cmsEntityComponentBodyFontValues: DEFAULT_BODY_FONT_VALUES,
    cmsEntityComponentBodyFont2Values: DEFAULT_BODY_FONT_VALUES,
    cmsEntityComponentHeaderFontStyles: { name: '', styles: '' },
    cmsEntityComponentBodyFontStyles: { name: '', styles: '' },
    cmsEntityComponentBodyFont2Styles: { name: '', styles: '' },
    backgroundColors: {
      nav: DEFAULT_BACKGROUND_COLOR,
      homepageContent: DEFAULT_BACKGROUND_COLOR,
      content: DEFAULT_BACKGROUND_COLOR,
      global: DEFAULT_BACKGROUND_COLOR,
    },
  },
  highlightedComponents: [],
  wedding: {},
  inPreview: false,
  pageDataContext: {},
};

// ACTIONS
const RECEIVE_WEDDING_VIEW = 'RECEIVE_WEDDING_VIEW';
const UPDATE_STATE = 'UPDATE_STATE';

type ReceiveWeddingAction = {
  type: typeof RECEIVE_WEDDING_VIEW;
  payload: WPublicWeddingView;
};
type UpdateStateAction = {
  type: typeof UPDATE_STATE;
  payload: Partial<WebsiteThemeContextStateType>;
};

type Action = ReceiveWeddingAction | UpdateStateAction;

export type WebsiteThemeContextType = {
  state: WebsiteThemeContextStateType;
  dispatch: (action: Action) => void;
};

const WebsiteThemeContext = createContext<WebsiteThemeContextType | undefined>({
  state: initialState,
  dispatch: () => null,
});

function websiteThemeReducer(state: WebsiteThemeContextStateType, action: Action) {
  switch (action.type) {
    case RECEIVE_WEDDING_VIEW: {
      return { ...state, wedding: action.payload };
    }
    case UPDATE_STATE: {
      return { ...state, ...action.payload };
    }
    default: {
      throw new Error(`Unhandled action type`);
    }
  }
}

export const WebsiteThemeContextProvider: React.FC<{
  initialState: WebsiteThemeContextStateType;
}> = ({ initialState: initialStateProp, children }) => {
  const [state, dispatch] = useReducer(websiteThemeReducer, initialStateProp || initialState);
  const value = {
    state,
    dispatch,
  };

  // TODO: RE-FACTOR ACTIONS LIFTING HERE
  useEffect(() => {
    dispatch({ type: UPDATE_STATE, payload: initialStateProp });
  }, [initialStateProp]);

  return <WebsiteThemeContext.Provider value={value}>{children}</WebsiteThemeContext.Provider>;
};

export const useWebsiteThemeContext = () => {
  const context = useContext(WebsiteThemeContext);
  if (context === undefined) {
    throw new Error('useWebsiteTheme must be used within a WebsiteThemeContextProvider');
  }

  const { state, dispatch } = context;

  const receiveWeddingView = (payload: WPublicWeddingView) => {
    return dispatch({ type: RECEIVE_WEDDING_VIEW, payload });
  };

  const updateState = (payload: Partial<WebsiteThemeContextStateType>) => {
    return dispatch({ type: UPDATE_STATE, payload });
  };

  return { state, actions: { receiveWeddingView, updateState } };
};
